import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import {
  AISummaryGenerateResponse,
  AISummaryListParams,
  AISummaryListResponse,
  AISummaryScope,
  GenerateAISummaryPayload,
} from 'src/interfaces/aiSummary.interface'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

const summaryPath = ({ facilityId, id, kind, subject }: AISummaryScope) => {
  const safeId = encodeURIComponent(id)
  switch (kind) {
    case 'adPerformance':
      return `/v1_private/ad-performance/accounts/${safeId}/summary`
    case 'aiListeningProfile':
      return `/v1_private/ai-listening/profiles/${safeId}/summary?subject=${encodeURIComponent(
        subject ?? id
      )}`
    case 'reviewAnalysis':
      return facilityId
        ? `/v1_private/review-analysis/campaign/${safeId}/facility/${encodeURIComponent(
            facilityId
          )}/summary`
        : `/v1_private/review-analysis/campaign/${safeId}/summary`
    case 'socialListening':
      return `/v1_private/campaign/${safeId}/summary`
    default:
      throw new Error(`Unknown AI summary scope: ${kind as string}`)
  }
}

class AISummaryApiService {
  private static _instance: AISummaryApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): AISummaryApiService {
    if (!AISummaryApiService._instance) {
      AISummaryApiService._instance = new AISummaryApiService()
    }
    return AISummaryApiService._instance
  }

  // params: the page's range, so the response carries that period's summary
  // and its own cooldown.
  public static async getSummaries(
    scope: AISummaryScope,
    params?: AISummaryListParams
  ): Promise<AISummaryListResponse> {
    const response =
      await AISummaryApiService.instance.client.get<AISummaryListResponse>(
        summaryPath(scope),
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async generateSummary(
    scope: AISummaryScope,
    payload: GenerateAISummaryPayload
  ): Promise<AISummaryGenerateResponse> {
    const response =
      await AISummaryApiService.instance.client.post<AISummaryGenerateResponse>(
        summaryPath(scope),
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }
}

export { AISummaryApiService }
